import React, { useState, useEffect } from 'react';
import { Plus, Edit2, Trash2, MapPin, X } from 'lucide-react';
import apiService from '../../apiService';

const INPUT_STYLE = { width:'100%',padding:'0.75rem',border:'1px solid #E9EBEF',borderRadius:'12px',fontSize:'0.875rem',outline:'none',boxSizing:'border-box' };

function ZoneFormModal({ zone, onClose, onSaved }) {
  const [form, setForm] = useState({
    name: zone?.name || '',
    description: zone?.description || '',
    base_fee: zone?.base_fee ?? 2000,
    fee_per_km: zone?.fee_per_km ?? 500,
    radius_km: zone?.radius_km ?? 10,
    latitude: zone?.latitude ?? -2.5083,
    longitude: zone?.longitude ?? 28.8608,
    is_active: zone?.is_active ?? true,
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    try {
      if (zone) {
        await apiService.put(`/admin/delivery/zones/${zone.id}`, form, { userType: 'admin' });
      } else {
        await apiService.post('/admin/delivery/zones', form, { userType: 'admin' });
      }
      onSaved();
    } catch (err) {
      setError(err.message || 'Erreur');
    } finally { setSaving(false); }
  };

  return (
    <div style={{position:'fixed',top:0,left:0,right:0,bottom:0,background:'rgba(0,0,0,0.5)',display:'flex',alignItems:'center',justifyContent:'center',zIndex:1000,padding:'1rem'}} onClick={onClose}>
      <div style={{background:'white',borderRadius:'16px',width:'100%',maxWidth:'440px',padding:'1.5rem',maxHeight:'90vh',overflow:'auto'}} onClick={e => e.stopPropagation()}>
        <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:'1rem'}}>
          <h2 style={{margin:0,fontSize:'1.2rem',fontWeight:'700'}}>{zone ? 'Modifier la zone' : 'Nouvelle zone'}</h2>
          <button onClick={onClose} style={{background:'none',border:'none',cursor:'pointer',color:'#9AA0A8'}}><X size={20}/></button>
        </div>
        {error && <div style={{background:'#fef2f2',color:'#dc2626',padding:'0.5rem',borderRadius:'8px',marginBottom:'0.75rem',fontSize:'0.875rem'}}>{error}</div>}
        <form onSubmit={handleSubmit} style={{display:'flex',flexDirection:'column',gap:'0.75rem'}}>
          <input value={form.name} onChange={e => setForm({...form, name:e.target.value})} placeholder="Nom de la zone" required style={INPUT_STYLE}/>
          <textarea value={form.description} onChange={e => setForm({...form, description:e.target.value})} placeholder="Description" rows={2} style={{...INPUT_STYLE,resize:'none'}}/>
          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'0.75rem'}}>
            <div>
              <label style={{display:'block',fontSize:'0.75rem',color:'#5C636E',marginBottom:'0.25rem'}}>Frais de base (FC)</label>
              <input type="number" value={form.base_fee} onChange={e => setForm({...form, base_fee:+e.target.value})} style={INPUT_STYLE}/>
            </div>
            <div>
              <label style={{display:'block',fontSize:'0.75rem',color:'#5C636E',marginBottom:'0.25rem'}}>Frais/km (FC)</label>
              <input type="number" value={form.fee_per_km} onChange={e => setForm({...form, fee_per_km:+e.target.value})} style={INPUT_STYLE}/>
            </div>
          </div>
          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr 1fr',gap:'0.75rem'}}>
            <div>
              <label style={{display:'block',fontSize:'0.75rem',color:'#5C636E',marginBottom:'0.25rem'}}>Latitude</label>
              <input type="number" step="0.0001" value={form.latitude} onChange={e => setForm({...form, latitude:+e.target.value})} style={INPUT_STYLE}/>
            </div>
            <div>
              <label style={{display:'block',fontSize:'0.75rem',color:'#5C636E',marginBottom:'0.25rem'}}>Longitude</label>
              <input type="number" step="0.0001" value={form.longitude} onChange={e => setForm({...form, longitude:+e.target.value})} style={INPUT_STYLE}/>
            </div>
            <div>
              <label style={{display:'block',fontSize:'0.75rem',color:'#5C636E',marginBottom:'0.25rem'}}>Rayon (km)</label>
              <input type="number" value={form.radius_km} onChange={e => setForm({...form, radius_km:+e.target.value})} style={INPUT_STYLE}/>
            </div>
          </div>
          <label style={{display:'flex',alignItems:'center',gap:'0.5rem',fontSize:'0.875rem',color:'#414852'}}>
            <input type="checkbox" checked={form.is_active} onChange={e => setForm({...form, is_active:e.target.checked})} style={{width:'16px',height:'16px'}}/>
            Zone active
          </label>
          <button type="submit" disabled={saving} style={{
            padding:'0.75rem',borderRadius:'12px',border:'none',background:'#FFB900',color:'white',
            fontWeight:'700',fontSize:'0.95rem',cursor:saving?'default':'pointer',opacity:saving?0.6:1
          }}>{saving ? 'Enregistrement...' : (zone ? 'Modifier' : 'Créer')}</button>
        </form>
      </div>
    </div>
  );
}

export default function DeliveryZonesPage() {
  const [zones, setZones] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const fetchZones = async () => {
    setLoading(true);
    try {
      const response = await apiService.get('/admin/delivery/zones', { userType: 'admin' });
      if (response.success) setZones(response.data || []);
    } catch {} finally { setLoading(false); }
  };

  useEffect(() => { fetchZones(); }, []);

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer cette zone ?')) return;
    try {
      await apiService.request(`/admin/delivery/zones/${id}`, { method: 'DELETE', userType: 'admin' });
      fetchZones();
    } catch { setError('Erreur lors de la suppression'); }
  };

  return (
    <div>
      <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:'1.5rem'}}>
        <div>
          <h2 style={{margin:0,fontSize:'1.5rem',fontWeight:'700',color:'#1E2229'}}>🗺️ Zones de Livraison</h2>
          <p style={{margin:'0.25rem 0 0',fontSize:'0.875rem',color:'#9AA0A8'}}>{zones.length} zone(s) configurée(s)</p>
        </div>
        <button onClick={() => { setEditing(null); setShowForm(true); }} style={{
          display:'flex',alignItems:'center',gap:'0.5rem',padding:'0.6rem 1.2rem',borderRadius:'12px',
          border:'none',background:'#FFB900',color:'white',fontWeight:'600',fontSize:'0.875rem',cursor:'pointer'
        }}><Plus size={18}/> Ajouter une zone</button>
      </div>

      {error && <div style={{background:'#fef2f2',color:'#dc2626',padding:'0.75rem 1rem',borderRadius:'8px',marginBottom:'1rem',fontSize:'0.875rem'}}>{error}</div>}

      <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill, minmax(320px, 1fr))',gap:'1rem'}}>
        {zones.map(zone => (
          <div key={zone.id} style={{background:'white',borderRadius:'16px',padding:'1.25rem',boxShadow:'0 1px 3px rgba(0,0,0,0.08)'}}>
            <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start',marginBottom:'0.75rem'}}>
              <div style={{display:'flex',alignItems:'center',gap:'0.5rem'}}>
                <div style={{width:'40px',height:'40px',borderRadius:'12px',background:'#fff7ed',display:'flex',alignItems:'center',justifyContent:'center'}}>
                  <MapPin size={18} color="#FFB900"/>
                </div>
                <div>
                  <h3 style={{margin:0,fontWeight:'600',color:'#1E2229'}}>{zone.name}</h3>
                  <p style={{margin:0,fontSize:'0.75rem',color:'#9AA0A8'}}>{zone.description || '—'}</p>
                </div>
              </div>
              <span style={{
                padding:'0.2rem 0.5rem',borderRadius:'12px',fontSize:'0.75rem',fontWeight:'600',
                background: zone.is_active ? '#ECFDF5' : '#fecaca', color: zone.is_active ? '#10B981' : '#dc2626'
              }}>{zone.is_active ? 'Active' : 'Inactive'}</span>
            </div>
            <div style={{display:'flex',flexDirection:'column',gap:'0.25rem',fontSize:'0.875rem',color:'#5C636E',marginBottom:'0.75rem'}}>
              <p style={{margin:0}}>Frais de base: <strong>{(zone.base_fee || 0).toLocaleString()} FC</strong></p>
              <p style={{margin:0}}>Par km: <strong>{(zone.fee_per_km || 0).toLocaleString()} FC</strong></p>
              <p style={{margin:0}}>Rayon: <strong>{zone.radius_km || '—'} km</strong></p>
            </div>
            <div style={{display:'flex',gap:'0.5rem'}}>
              <button onClick={() => { setEditing(zone); setShowForm(true); }} style={{
                flex:1,padding:'0.5rem',borderRadius:'12px',border:'none',background:'#fff7ed',color:'#FFB900',
                fontWeight:'600',fontSize:'0.875rem',cursor:'pointer',display:'flex',alignItems:'center',justifyContent:'center',gap:'0.35rem'
              }}><Edit2 size={14}/> Modifier</button>
              <button onClick={() => handleDelete(zone.id)} style={{
                padding:'0.5rem 0.75rem',borderRadius:'12px',border:'none',background:'#fef2f2',color:'#DC2626',cursor:'pointer'
              }}><Trash2 size={14}/></button>
            </div>
          </div>
        ))}
      </div>

      {zones.length === 0 && !loading && (
        <div style={{textAlign:'center',padding:'3rem',color:'#9AA0A8'}}>
          <p style={{fontSize:'2rem',marginBottom:'0.5rem'}}>🗺️</p>
          <p>Aucune zone de livraison configurée</p>
        </div>
      )}

      {showForm && <ZoneFormModal zone={editing} onClose={() => setShowForm(false)} onSaved={() => { setShowForm(false); fetchZones(); }} />}
    </div>
  );
}
